import { useEffect, useRef, useState } from 'react'
import { useConfigurationContext } from '@nv/contexts'
import CirclePlusIcon from '@nv/images/design/circle-plus-icon'
import DeleteIcon from '@nv/images/design/delete-icon'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styles from './UploadDocs.module.scss'

export const UploadDocs = ({ setFieldValue, fileField1, fileField2, formik, req, acceptedFilesLink, onClear = false }) => {
  const { t } = useTranslation()
  const { registrationIncludeSupportingDocument } = useConfigurationContext()
  const fileInputRef1 = useRef(null)
  const fileInputRef2 = useRef(null)

  // Initialize the state to load from sessionStorage (if available)
  const [filePreview1, setFilePreview1] = useState(() => {
    return sessionStorage.getItem(fileField1) || null
  })
  const [filePreview2, setFilePreview2] = useState(() => {
    return sessionStorage.getItem(fileField2) || null
  })

  const handleFileUpload = (ref) => {
    if (ref && ref.current) {
      ref.current.click()
    }
  }

  const handleFileChange = (event, setFilePreview, fieldName) => {
    const file = event.target.files[0]
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      alert('Please upload a valid image or pdf file (JPG, PNG, PDF etc.).')
      return
    }

    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result

        if (typeof base64String === 'string') {
          setFilePreview(base64String)
          setFieldValue(fieldName, file)

          sessionStorage.setItem(fieldName, base64String)
        } else {
          console.error('Error: The result from FileReader is not a string')
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveFile = (setFilePreview, fieldName) => {
    setFilePreview(null)
    setFieldValue(fieldName, null)
    sessionStorage.removeItem(fieldName)
  }

  useEffect(() => {
    handleRemoveFile(setFilePreview1, fileField1)
    handleRemoveFile(setFilePreview2, fileField2)
  }, [onClear])

  return (
    <div className={styles['container']}>
      <button
        type='button'
        className={clsx(styles['upload-item'], {
          [styles['required']]: req,
        })}
        onClick={() => handleFileUpload(fileInputRef1)}
      >
        {filePreview1 ? (
          <>
            <DeleteIcon onClick={() => handleRemoveFile(setFilePreview1, fileField1)} />
            <img src={filePreview1} alt='Proof of Identity' className={styles['file-preview']} />
          </>
        ) : (
          <>
            <CirclePlusIcon />
            <span>{t('registration.uploadProof')}</span>
          </>
        )}
        <input
          ref={fileInputRef1}
          type='file'
          accept='image/* application/pdf'
          style={{ display: 'none' }}
          onChange={(e) => handleFileChange(e, setFilePreview1, fileField1)}
        />
      </button>
      {formik.errors.proofOfIdentity && formik.touched.proofOfIdentity && (
        <div className={styles['error']}>{formik.errors.proofOfIdentity}</div>
      )}

      {registrationIncludeSupportingDocument && (
        <>
          <button type='button' className={styles['upload-item']} onClick={() => handleFileUpload(fileInputRef2)}>
            {filePreview2 ? (
              <>
                <DeleteIcon onClick={() => handleRemoveFile(setFilePreview2, fileField2)} />
                <img src={filePreview2} alt='Supported Document' className={styles['file-preview']} />
              </>
            ) : (
              <>
                <CirclePlusIcon />
                <span>{t('registration.uploadSupportingDoc')}</span>
              </>
            )}
            <input
              ref={fileInputRef2}
              type='file'
              accept='image/* application/pdf'
              style={{ display: 'none' }}
              onChange={(e) => handleFileChange(e, setFilePreview2, fileField2)}
            />
          </button>
          {formik.errors.supportedDocument && formik.touched.supportedDocument && (
            <div className={styles['error']}>{formik.errors.supportedDocument}</div>
          )}
        </>
      )}
      <div>
        Your uploaded identification document must include your <strong>full legal name and residential address.</strong> Some examples of
        acceptable identification containing both are an Alberta driver's licence or ID card, a statement of government benefits, a utility
        bill, a residential lease or mortgage statement, or a vehicle ownership, registration or insurance certificate.
      </div>
      {acceptedFilesLink && (
        <div className={styles['upload-text']}>
          <a target='_blank' href={acceptedFilesLink} rel='noreferrer'>
            {t('registration.acceptDocs')}
          </a>
        </div>
      )}
    </div>
  )
}
